import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// ─────────────────────────────────────────────────────────────────────────────
// Fetch logged-in user's orders
// ─────────────────────────────────────────────────────────────────────────────
export const fetchUserOrders = createAsyncThunk("orders/fetchUserOrders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/orders/my-orders`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to fetch user orders");
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Fetch order details by ID
// ─────────────────────────────────────────────────────────────────────────────
export const fetchOrderDetails = createAsyncThunk("orders/fetchOrderDetails",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to fetch order details");
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Fetch live tracking data for a shipment
// Called with the AWB / tracking number stored on the order
// ─────────────────────────────────────────────────────────────────────────────
export const fetchTracking = createAsyncThunk("orders/fetchTracking",
  async (awb, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/orders/track/${awb}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch tracking info"
      );
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────────────────────────────────────
const orderSlice = createSlice({
  name: "orders",
  initialState: {
    orders: [],
    totalOrders: 0,
    orderDetails: null,
    loading: false,
    error: null,

    // Tracking state
    trackingData: null,
    trackingLoading: false,
    trackingError: null,
  },
  reducers: {
    clearOrderDetails: (state) => {
      state.orderDetails = null;
    },
    clearTrackingData: (state) => {
      state.trackingData = null;
      state.trackingError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchUserOrders ──────────────────────────────────────────────────────
      .addCase(fetchUserOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
        state.totalOrders = action.payload.length;
      })
      .addCase(fetchUserOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })

      // ── fetchOrderDetails ────────────────────────────────────────────────────
      .addCase(fetchOrderDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload;
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
        state.orderDetails = null;
      })

      // ── fetchTracking ────────────────────────────────────────────────────────
      .addCase(fetchTracking.pending, (state) => {
        state.trackingLoading = true;
        state.trackingError = null;
      })
      .addCase(fetchTracking.fulfilled, (state, action) => {
        state.trackingLoading = false;
        state.trackingData = action.payload;
      })
      .addCase(fetchTracking.rejected, (state, action) => {
        state.trackingLoading = false;
        state.trackingError = action.payload || "Could not load tracking info";
        state.trackingData = null;
      });
  },
});

export const { clearOrderDetails, clearTrackingData } = orderSlice.actions;
export default orderSlice.reducer;
